import React, { useState, useEffect } from 'react';
import { useLocation, useHistory, withRouter } from 'react-router-dom';
import { Tabs, Badge, Drawer } from 'antd';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import H3Text from '@Uilib/h3Text';
import H3Image from '@Uilib/h3Image';
import closeIcon from '@Images/icons/icon-close-blue.png';
import { SettingOutlined } from '@ant-design/icons';
import { socket } from '@Apis/constants';
import Lottie from 'lottie-react';
import connectedAnimation from '../../../media/lotties/connected.json';
import disconnectedAnimation from '../../../media/lotties/disconnected.json';
import TallyMappedDocuments from './TallyMappedDocuments';
import TallyUnMappedDocuments from './TallyUnMappedDocuments';
import AccountSetup from '../../Admin/Integration/TallyIntegration/TallySetup/Steps/AccountSetup';
import useCheckPortrait from '../../../hooks/useCheckPortrait';
import './style.scss';

const { TabPane } = Tabs;

const TallyIntegration = (props) => {
  const {
    user, match,
  } = props;
  const location = useLocation();
  const history = useHistory();

  const [currentTab, setCurrentTab] = useState('/unmapped');
  const [currentDocumentState, setCurrentDocumentState] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);

  const [count, setCount] = useState({
    unMappedCount: 0,
    readyToPushCount: 0,
    completedCount: 0,
    errorCount: 0,
    excludedDocCount: 0,
  });

  const {
    isMobile, isPortrait, isMobileAndPortrait, isMobileOrPortrait,
  } = useCheckPortrait();

  useEffect(() => {
    const tab = new URLSearchParams(location.search).get('tab');
    if (tab === '/ready-to-push') setCurrentDocumentState('PENDING');
    if (tab === '/completed') setCurrentDocumentState('COMPLETED');
    if (tab === '/errors') setCurrentDocumentState('ERROR');
    if (tab === '/excluded-documents') setCurrentDocumentState('EXCLUDE');
    setCurrentTab(tab || '/unmapped');
  }, [location.search, user, showDrawer]);

  useEffect(() => {
    // MOVE Define a function to emit the 'register' event
    const register = () => {
      socket.emit('register', { user_id: user.user_id, it_id: user.tenant_info?.tally_configuration?.it_id });
    };

    // MOVE Emit the 'register' event when the component mounts
    register();

    socket.on('tally_client_status', (status) => {
      if (user.tenant_info?.tally_configuration?.it_id === status.it_id) {
        setIsConnected(status.online === true);
      }
    });

    // MOVE Listen for 'disconnect' event
    socket.on('disconnect', (data) => {
      setIsConnected(false);
    });

    // MOVE Clean up the socket listeners on component unmount
    return () => {
      socket.off('tally_client_status');
      socket.off('disconnect');
    };
  }, []);

  return (
    <React.Fragment>
      <Drawer
        open={showDrawer}
        width={isPortrait? '400px' : '1050px'}
        mask
        onClose={() => setShowDrawer(false)}
        destroyOnClose
      >
        <div className="custom-drawer__header-wrapper">
          <div className="custom-drawer__header" style={{ width: isPortrait? '350px' : '1000px' }}>
            <H3Text text="Configuration" />
            <H3Image src={closeIcon} className="custom-drawer__close-icon" onClick={() => setShowDrawer(false)} />
          </div>
        </div>
        <div className="tally-ledger-config">
          <AccountSetup
            itId={user?.tenant_info?.it_id}
            isDrawer
            setShowDrawer={setShowDrawer}
          />
        </div>
      </Drawer>
      <div className="doc-list__tabs-wrapper view-tally__wrapper">
        <React.Fragment>
          <div className="tally-tabs__wrapper">
            <div className="tab-area">
              <Tabs
                defaultActiveKey="1"
                activeKey={currentTab}
                onChange={(key) => {
                  setCurrentTab(key);
                  history.push({
                    pathname: location.pathname,
                    search: `?tab=${key}`,
                  });
                }}
              >
                <TabPane
                  tab={(
                    <React.Fragment>
                      Unmapped
                      {' '}
                      <Badge count={count?.unMappedCount} overflowCount={999} />
                    </React.Fragment>
                  )}
                  key="/unmapped"
                />
                <TabPane
                  tab={(
                    <React.Fragment>
                      Ready to Push
                      {' '}
                      <Badge count={count?.readyToPushCount} overflowCount={999} />
                    </React.Fragment>
                  )}
                  key="/ready-to-push"
                />
                <TabPane
                  tab={(
                    <React.Fragment>
                      Completed
                      {' '}
                      <Badge count={count?.completedCount} overflowCount={999} />
                    </React.Fragment>
                  )}
                  key="/completed"
                />
                <TabPane
                  tab={(
                    <React.Fragment>
                      Errors
                      {' '}
                      <Badge count={count?.errorCount} overflowCount={999} />
                    </React.Fragment>
                  )}
                  key="/errors"
                />
                <TabPane
                  tab={(
                    <React.Fragment>
                      Exclude Documents
                      {' '}
                      <Badge count={count?.excludedDocCount} overflowCount={999} />
                    </React.Fragment>
                  )}
                  key="/excluded-documents"
                />
              </Tabs>
            </div>
            <div className="tally-header">
              <div className="tally-header__left">
                <div
                  onClick={() => setShowDrawer(true)}
                  className="tally__reconfigure-button"
                >
                  <SettingOutlined />
                </div>
              </div>
              <div className="tally-header__right">
                <H3Text
                  text={(
                    <React.Fragment>
                      <div className={`tally-status__wrapper ${isConnected ? '' : 'tally-status__wrapper-inactive'}`}>
                        {isConnected ? (
                          <Lottie
                            animationData={connectedAnimation}
                            loop
                          />
                        ) : (
                          <Lottie
                            animationData={disconnectedAnimation}
                            loop
                          />
                        )}
                        <div style={{ marginLeft: '3px' }}>
                          <span style={{ color: isConnected ? '#00B96B' : 'darkgrey', fontWeight: '600' }}>
                            {' '}
                            {isConnected ? 'Tally Connected' : 'Tally Connection Unavailable'}
                          </span>
                          <br />
                          <span
                            title={user.tenant_info?.tally_configuration?.company_details?.name || ''}
                            style={{
                              fontSize: '10px',
                              maxWidth: '130px',
                              display: 'inline-block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              border: 'none',
                            }}
                          >
                            {user.tenant_info?.tally_configuration?.company_details?.name || ''}
                          </span>
                        </div>
                      </div>

                    </React.Fragment>
                  )}
                  style={{
                    color: isConnected ? '#00B96B' : 'darkgrey',
                    padding: '3px 10px',
                    fontSize: '12px',
                    borderRadius: '5px',
                  }}
                />
              </div>
            </div>
          </div>

          {currentTab === '/unmapped' && (
            <React.Fragment>
              <TallyUnMappedDocuments
                currentTab={currentTab}
                counter={setCount}
                showDrawer={showDrawer}
              />
            </React.Fragment>
          )}
          {currentTab === '/ready-to-push' && (
            <React.Fragment>
              <TallyMappedDocuments
                currentDocumentState={currentDocumentState}
                currentTab={currentTab}
                counter={setCount}
                isConnected={isConnected}
                showDrawer={showDrawer}
              />
            </React.Fragment>
          )}
          {currentTab === '/completed' && (
            <React.Fragment>
              <TallyMappedDocuments
                currentDocumentState={currentDocumentState}
                currentTab={currentTab}
                counter={setCount}
                isConnected={isConnected}
                showDrawer={showDrawer}
              />
            </React.Fragment>
          )}
          {currentTab === '/errors' && (
            <React.Fragment>
              <TallyMappedDocuments
                currentDocumentState={currentDocumentState}
                currentTab={currentTab}
                counter={setCount}
                isConnected={isConnected}
                showDrawer={showDrawer}
              />
            </React.Fragment>
          )}
          {currentTab === '/excluded-documents' && (
            <React.Fragment>
              <TallyMappedDocuments
                currentDocumentState={currentDocumentState}
                currentTab={currentTab}
                counter={setCount}
                showDrawer={showDrawer}
              />
            </React.Fragment>
          )}
        </React.Fragment>
      </div>

    </React.Fragment>
  );
};

const mapStateToProps = ({
  UserReducers,
}) => ({
  user: UserReducers.user,
});

const mapDispatchToProps = (dispatch) => ({
  // Map your dispatch actions here
});

TallyIntegration.propTypes = {
  user: PropTypes.any,
  match: PropTypes.object.isRequired, // Adjust the prop type as per your requirement
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(TallyIntegration));
